import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { FiTrash2 } from "react-icons/fi";
import { useParams } from "next/navigation";
import { Button, Dialog, DialogBody, DialogContent, DialogTrigger, Input, Label } from "@/components/ui";
import { DialogClose, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/contexts/toaster";
import { parseError } from "@/lib/parse-error";
import { PatientDynamicFieldType } from "@/types/enums/patient-health-field";
import { useUpdatePatientDynamicFieldsMutation } from "@/api/provider/user/patient/patient-mutation";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
const RECOGNIZED_TYPES: string[] = Object.values(PatientDynamicFieldType);
const isUnrecognizedField = (fieldData: any) => {
  const type = fieldData?.type;
  return !type || !RECOGNIZED_TYPES.includes(type);
};
interface DynamicFieldsDialogProps {
  children?: React.ReactNode;
}

/**
 * One entry per stored row, keyed by field ID. These fields hold a list —
 * `patient_dynamic_field_value` is one row per value — so editing them through a
 * single string truncated the list to its first row on read and deleted the rest
 * on save.
 *
 * Keyed by ID rather than display name because react-hook-form treats "." and "[]"
 * in a field name as a path: a tenant field named "Dr. Notes" would submit as
 * `{ Dr: { " Notes": [...] } }`, the field ID lookup would miss, and the user's
 * edits would be dropped with no error.
 */
interface DynamicFieldsForm {
  [fieldId: string]: string[];
}
const DynamicFieldsDialog: React.FC<DynamicFieldsDialogProps> = ({
  children
}) => {
  const [isLoading, setIsLoading] = useState<{
    patient: boolean;
  }>({
    patient: false
  });
  const params = useParams();
  const patientId = params?.id as string || "";
  const {
    data: patient
  } = useQuery({
    ...patientQueryOptions.detail(patientId),
    enabled: !!patientId
  });
  const {
    toast
  } = useToast();
  const [open, setOpen] = useState(false);

  // Only include unrecognized-type fields in the form to avoid
  // overwriting recognized fields (medications, allergies, etc.)
  const filteredValues = useMemo(() => {
    if (!patient?.healthData) return {};
    const formData: DynamicFieldsForm = {};
    for (const fieldData of Object.values(patient.healthData)) {
      if (!isUnrecognizedField(fieldData) || !fieldData?.fieldId) continue;
      formData[fieldData.fieldId] = (fieldData?.values ?? []).map(String).filter(value => value.trim());
    }
    return formData;
  }, [patient?.healthData]);
  const methods = useForm<DynamicFieldsForm>({
    defaultValues: filteredValues
  });
  const {
    handleSubmit,
    reset
  } = methods;
  useEffect(() => {
    reset(filteredValues);
  }, [filteredValues, reset]);
  const updateDynamicFieldsMutation = useUpdatePatientDynamicFieldsMutation(patientId);
  const showPatientNotFoundToast = useCallback(() => {
    toast({
      title: "Patient Not Found",
      description: `Patient with ID ${patient?.id} not found.`,
      variant: "error"
    });
  }, [patient?.id, toast]);
  const handleFormSubmit = useCallback(async (data: DynamicFieldsForm) => {
    setIsLoading(prev => ({
      ...prev,
      patient: true
    }));
    if (!patient) {
      showPatientNotFoundToast();
      setIsLoading(prev_0 => ({
        ...prev_0,
        patient: false
      }));
      return false;
    }
    try {
      // The form is already keyed by field ID, which is what the API takes.
      const dynamicFieldsById: Record<string, string[]> = {};
      Object.entries(data).forEach(([fieldId, value_0]) => {
        // Send every entry. This previously sent [value.trim()] — a single
        // element — for each field in the form, and the form holds every
        // unrecognized field, so saving deleted rows 2..N of fields the user
        // had not even edited (PUT /dynamic-fields replaces per field).
        //
        // Deduped over the whole array rather than only on entry, so legacy or
        // imported rows differing just by case collapse too. Duplicate rows make
        // the dedicated editors' collision guards discard later edits silently,
        // and both backend writers dedupe the same way.
        const seen = new Set<string>();
        dynamicFieldsById[fieldId] = (value_0 ?? []).map(entry => entry.trim()).filter(entry_0 => {
          if (!entry_0) return false;
          const key = entry_0.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });
      const response = await updateDynamicFieldsMutation.mutateAsync(dynamicFieldsById);
      if (response) {
        setOpen(false);
      } else {
        toast({
          title: "Update Failed",
          description: "Failed to update patient health information.",
          variant: "error"
        });
      }
    } catch (error) {
      const message = await parseError(error);
      toast({
        title: "Update Failed",
        description: message,
        variant: "error"
      });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        patient: false
      }));
    }
  }, [patient, toast, setIsLoading, showPatientNotFoundToast]);
  if (!patient?.healthData) return null;
  return <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild onClick={() => setOpen(true)}>
        {children}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Health Information</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <FormProvider {...methods}>
            <form id="dynamic-fields-form" onSubmit={handleSubmit(async data_0 => await handleFormSubmit(data_0))} className="space-y-4" noValidate>
              <DynamicFieldsSection healthData={patient.healthData} />
            </form>
          </FormProvider>
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" form="dynamic-fields-form" isLoading={isLoading.patient}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
};
const DynamicFieldsSection = t0 => {
  const $ = _c(12);
  const {
    healthData
  } = t0;
  const {
    control,
    formState: t1
  } = useFormContext();
  const {
    errors
  } = t1;
  const formatFieldName = _temp2;
  let filteredFields;
  let t2;
  let t3;
  if ($[0] !== control || $[1] !== errors || $[2] !== healthData) {
    filteredFields = Object.entries(healthData).filter(_temp3).map(_temp4).filter(_temp5);
    t2 = "space-y-4";
    t3 = filteredFields.map(t4 => {
      const {
        name: name_0,
        fieldId: fieldKey
      } = t4;
      return <div key={fieldKey}><Label htmlFor={fieldKey}>{formatFieldName(name_0)}</Label><Controller name={fieldKey} control={control} render={t5 => {
          const {
            field: field_1
          } = t5;
          const entries = field_1.value ?? [];
          const label = formatFieldName(name_0).toLowerCase();
          return <div className="space-y-2">{entries.map((entry, index) => <div key={index} className="flex items-start gap-2"><Input id={index === 0 ? fieldKey : undefined} value={entry} onChange={event => {
                const next = [...entries];
                next[index] = event.target.value;
                field_1.onChange(next);
              }} placeholder={`Enter ${label}`} className="w-full" hasError={!!errors[fieldKey]} errorMessage={index === 0 ? errors[fieldKey]?.message as string : undefined} /><Button type="button" variant="ghost" aria-label={`Remove ${label} entry`} onClick={() => field_1.onChange(entries.filter((_, i) => i !== index))}><FiTrash2 className="h-4 w-4" /></Button></div>)}<Button type="button" variant="secondary" onClick={() => field_1.onChange([...entries, ""])}>{entries.length ? `Add another ${label}` : `Add ${label}`}</Button></div>;
        }} /></div>;
    });
    $[0] = control;
    $[1] = errors;
    $[2] = healthData;
    $[3] = filteredFields;
    $[4] = t2;
    $[5] = t3;
  } else {
    filteredFields = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== filteredFields.length) {
    t4 = filteredFields.length === 0 && <p className="text-body text-muted-foreground">All available health fields already have dedicated sections above.</p>;
    $[6] = filteredFields.length;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
export default DynamicFieldsDialog;
function _temp(c) {
  return c.toUpperCase();
}
function _temp2(field) {
  return field.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^\w/, _temp);
}
function _temp3(t0) {
  const [, fieldData] = t0;
  return isUnrecognizedField(fieldData);
}
function _temp4(t0) {
  const [name, fieldData_0] = t0;
  return {
    name,
    fieldId: fieldData_0?.fieldId as string
  };
}
function _temp5(field_0) {
  return !!field_0.fieldId;
}